import MaterialTable from '../../Components/DataTable/DataTable';
import apiClient, { getAccessToken } from '../../apiClient';
import React from "react";
import Autocomplete from '@material-ui/lab/Autocomplete';
import { exportInvoicePdf } from "../../Components/invoicePdf";
import { CSVLink } from "react-csv";
import { TableFooter } from '@material-ui/core';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@material-ui/core';
import Dialog  from "@material-ui/core/Dialog"; 
import DialogActions from "@material-ui/core/DialogActions"; 
import DialogContent from "@material-ui/core/DialogContent"; 
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { withStyles } from "@material-ui/core/styles";
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import DateFnsUtils from '@date-io/date-fns';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import InputLabel from '@material-ui/core/InputLabel';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import './Transactions.scss';
import Button from '@material-ui/core/Button';
import moment from 'moment';
import Spinner from '../Spinner';
import ReactDOM from 'react-dom';
import CheckIn from '../CheckIn/CheckIn';
import CheckOut from '../CheckOut/CheckOut';
import TransactionHeader from '../../Components/TransactionHeader';
import InvoiceLineRows from '../../Components/InvoiceLineRows';
import { SHOW_PAYMENT_FEE } from '../../Components/displaySettings';

import { AppBar, Tab, Tabs, Typography, Box } from "@material-ui/core";

/** Set to false to hide Product Adjustments without touching DB permissions; must match logic in normalizeTransactionTabIndex. */
const SHOW_ADJUSTMENT_TAB = true;

function userHasAdjustmentPermission(user) {
  if (user == null || !Array.isArray(user.permissions)) return false;
  return user.permissions.some(
    (x) => x.PermissionName === 'Adjustment Product' && x.PermissionAllowed === 1
  );
}

function userHasLookupPermission(user) {
  if (user == null || !Array.isArray(user.permissions)) return false;
  return user.permissions.some(
    (x) => x.PermissionName === 'Show Lookup Item' && x.PermissionAllowed === 1
  );
}

function isAdjustmentTabVisible(user) {
  return SHOW_ADJUSTMENT_TAB && userHasAdjustmentPermission(user);
}

/** Logical indices 0–5 stay stable when optional tabs are omitted; fixes MUI child-order vs saved tabindex. */
function normalizeTransactionTabIndex(idx, user) {
  if (user == null || !Array.isArray(user.permissions)) return idx <= 3 ? idx : 3;
  const canAdj = isAdjustmentTabVisible(user);
  const canLook = userHasLookupPermission(user);
  if (idx === 4 && !canAdj) return canLook ? 5 : 3;
  if (idx === 5 && !canLook) return canAdj ? 4 : 3;
  return idx;
}

const transactionStyles = {
  customTextField: {
    '& .MuiInputBase-root': {
      backgroundColor: 'transparent',
    },
  },
};

/** Right padding for last numeric column so values are not flush to the grid edge */
const TABLE_LAST_COL_STYLE = { paddingRight: 32, textAlign: 'right' };

const parseTranDetailNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const normalizeTranCommissionType = (type) => {
  const t = String(type || '').trim().toLowerCase();
  if (t === 'percentage' || t === 'percent' || t === 'profit') return 'Percentage';
  if (t === 'rate') return 'Rate';
  if (t === 'amount' || t === 'individual' || t === 'fixed') return 'Individual';
  return '';
};

const getTranItemQty = (item) =>
  parseTranDetailNumber(item?.Quantity ?? item?.checkoutqty ?? item?.qty ?? 0);

const getTranItemTotal = (item) =>
  parseTranDetailNumber(item?.TotalPrice ?? item?.totpri ?? 0);

const getTranItemUnitPrice = (item) =>
  parseTranDetailNumber(item?.PricePerUnit ?? item?.priperunit ?? 0);

const mapInvoiceCommissionsFromApi = (rows) =>
  (rows || []).map((c) => {
    const rawType = c.Type != null ? String(c.Type).trim() : '';
    let commissionType = normalizeTranCommissionType(rawType);
    if (!commissionType) {
      if (c.Rate != null && c.Rate !== '') commissionType = 'Rate';
      else if (c.Amount != null && c.Amount !== '') commissionType = 'Individual';
      else if (c.Percentage != null && c.Percentage !== '') commissionType = 'Percentage';
      else commissionType = 'Individual';
    }
    return {
      commissionId: c.CommissionId,
      commissionName: c.CommissionName || '',
      commissionType,
      percent: c.Percentage != null ? c.Percentage : '',
      rate: c.Rate != null ? c.Rate : '',
      amount: c.Amount != null ? c.Amount : '',
      commissionAmount: c.CommissionAmount != null ? c.CommissionAmount : 0,
      description: c.Description != null ? c.Description : '',
    };
  });

/** Rate + Individual added; Percentage (profit) subtracted. */
const resolveTranCommissionKind = (c) => {
  const normalized = normalizeTranCommissionType(
    c?.commissionType ?? c?.Type ?? c?.type
  );
  if (normalized === 'Rate' || normalized === 'Individual') return 'fixed';
  if (normalized === 'Percentage') return 'profit';

  const hasRate =
    (c?.rate != null && String(c.rate).trim() !== '') ||
    (c?.Rate != null && String(c.Rate).trim() !== '');
  const hasAmount =
    (c?.amount != null && String(c.amount).trim() !== '') ||
    (c?.Amount != null && String(c.Amount).trim() !== '');
  const hasPercent =
    (c?.percent != null && String(c.percent).trim() !== '') ||
    (c?.Percentage != null && String(c.Percentage).trim() !== '') ||
    (c?.percentage != null && String(c.percentage).trim() !== '');

  if (hasRate || hasAmount) return 'fixed';
  if (hasPercent) return 'profit';
  return 'fixed';
};

const getTranCommissionLineAmountValue = (c, itemRows) => {
  let amount = parseTranDetailNumber(c?.commissionAmount);
  if (amount > 0) return amount;

  const type = normalizeTranCommissionType(c?.commissionType ?? c?.Type ?? c?.type);
  if (type === 'Rate' || (type === '' && resolveTranCommissionKind(c) === 'fixed')) {
    const rate = parseTranDetailNumber(c?.rate ?? c?.Rate);
    if (rate > 0 && itemRows?.length) {
      const totalQty = itemRows.reduce((sum, item) => sum + getTranItemQty(item), 0);
      if (totalQty > 0) return Math.round(rate * totalQty * 100) / 100;
    }
  }
  if (type === 'Individual' || resolveTranCommissionKind(c) === 'fixed') {
    const individual = parseTranDetailNumber(c?.amount ?? c?.Amount);
    if (individual > 0) return individual;
  }
  return 0;
};

const sumTranCommissionAmountsByKind = (commissionList, itemRows) => {
  let profitTotal = 0;
  let fixedTotal = 0;
  (commissionList || []).forEach((c) => {
    const amount = getTranCommissionLineAmountValue(c, itemRows);
    if (resolveTranCommissionKind(c) === 'profit') {
      profitTotal += amount;
    } else {
      fixedTotal += amount;
    }
  });
  return {
    profitTotal,
    fixedTotal,
    total: profitTotal + fixedTotal,
  };
};

const applyTranCommissionToTicketTotal = (
  itemSubtotal,
  paymentFee,
  commissionList,
  itemRows
) => {
  const { profitTotal, fixedTotal } = sumTranCommissionAmountsByKind(
    commissionList,
    itemRows
  );
  return itemSubtotal + (paymentFee || 0) - profitTotal + fixedTotal;
};

/**
 * Client invoice (Rate / Individual): commission ÷ total qty added to each unit price.
 */
const distributeFixedCommissionIntoTransactionItems = (items, fixedCommissionAmount) => {
  if (!items || !items.length) return [];
  const commission = parseTranDetailNumber(fixedCommissionAmount);
  if (commission <= 0) {
    return items.map((item) => ({ ...item }));
  }

  const totalQty = items.reduce((sum, item) => sum + getTranItemQty(item), 0);
  const perUnitCommission = totalQty > 0 ? commission / totalQty : 0;
  let distributed = 0;

  return items.map((item, idx) => {
    const baseTot = getTranItemTotal(item);
    const basePrice = getTranItemUnitPrice(item);
    const qty = getTranItemQty(item);
    let addAmount;

    if (totalQty <= 0) {
      addAmount =
        idx === items.length - 1
          ? Math.round((commission - distributed) * 100) / 100
          : Math.round((commission / items.length) * 100) / 100;
    } else if (idx === items.length - 1) {
      addAmount = Math.round((commission - distributed) * 100) / 100;
    } else {
      addAmount = Math.round(perUnitCommission * qty * 100) / 100;
    }
    distributed += addAmount;

    const newTotal = Math.round((baseTot + addAmount) * 100) / 100;
    const newPrice =
      qty > 0
        ? Math.round((newTotal / qty) * 100) / 100
        : Math.round((basePrice + perUnitCommission) * 100) / 100;

    return {
      ...item,
      TotalPrice: newTotal,
      totpri: newTotal,
      PricePerUnit: newPrice,
      priperunit: newPrice,
    };
  });
};

class Transactions extends React.Component {

  constructor(props) {
    super(props);
    // Create a ref for the content
    this.contentRef = React.createRef();
    this.gridRef = React.createRef();
    this.invoiceTypeToggleRef = React.createRef();
    this.csvLinkRef = React.createRef();
  }

  toQty = (value) => {
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  };

  isReserveOrderCreated = (rowData) => {
    const v = rowData?.IsOrderCreated;
    return v === 1 || v === true || String(v) === '1';
  };

  isTicketEditable = (rowData) => {
    const createdAt = rowData?.CreatedAt;
    if (createdAt) {
      const created = moment(createdAt, ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true);
      if (created.isValid()) {
        return moment().diff(created, 'milliseconds') < 24 * 60 * 60 * 1000;
      }
    }
    const canEdit = rowData?.CanEdit;
    return canEdit === 1 || canEdit === true || String(canEdit) === '1';
  };

  renderTicketEditLink = (rowData) => {
    if (!this.isTicketEditable(rowData)) return null;
    return (
      <a
        href="#"
        onClick={(event) => {
          event.preventDefault();
          this.handleLinkEditClick(rowData, 'edit');
        }}
      >
        Edit
      </a>
    );
  };

  renderReserveOrderTicketReference = (rowData) => {
    if (!this.isReserveOrderCreated(rowData)) return '';
    const orderTicket = rowData?.OrderTicketNumber;
    if (!orderTicket) return '—';
    return (
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          this.handleLinkClick({ TicketNumber: orderTicket });
        }}
      >
        {orderTicket}
      </a>
    );
  };

  reserveOrderTicketColumn = {
    title: 'Order Ticket',
    field: 'OrderTicketNumber',
    render: (rowData) => this.renderReserveOrderTicketReference(rowData),
  };
 
  
 
    state = {
        Results: [],
    exportData: [],
        TicketDetail:[],
        apiItemData:[],
        itemLookupLoading: false,
        editWindowNow: Date.now(),
        submitted: false,
        columns: [
            { title: 'Ticket Number', field: 'TicketNumber' },
            { title: 'PO Number', field: 'PONumber' },
            { title: 'Date', field: 'Date' , render: rowData => moment(rowData.Date).format('DD-MM-YYYY')},
            { title: 'User Name', field: 'UserName' },
            { title: 'Vendor Name', field: 'VendorName' },
            { title: 'Customer Name', field: 'CustomerName' },
            { title: 'Item Name', field: 'ItemName' },
            { title: 'Item Id', field: 'ItemId' , hidden:true },
            { title: 'Item Id', field: 'VendorId' , hidden:true },
            { title: 'Item Id', field: 'CustomerId' , hidden:true },
            { title: 'Item Id', field: 'PONumber' , hidden:true },
            { title: 'Item Id', field: 'Reason' , hidden:true },
            { title: 'Item Id', field: 'ReturnTicketNumber' , hidden:true },
            { title: 'Item Id', field: 'TargetPrice' , hidden:true },
            { title: 'Item Number', field: 'ItemNumber' },
            { title: 'Price Per Unit', field: 'PricePerUnit', align: 'right' , render: rowData =>  <span>${rowData.PricePerUnit.toLocaleString()}</span> },
            { title: 'Quantity', field: 'Quantity', align: 'right' },
            { title: 'Total Price', field: 'TotalPrice', align: 'right', cellStyle: TABLE_LAST_COL_STYLE, headerStyle: TABLE_LAST_COL_STYLE, render: rowData => <span>${rowData.TotalPrice.toLocaleString()}</span>},
        ],
        headers: [
          { label: "Ticket Number", key: "TicketNumber" },
          { label: "PO Number", key: "PONumber" },
          { label: "Date", key: "Date" },
          { label: "User Name", key: "UserName" },
          { label: "Vendor Name", key: "VendorName" },
          { label: "Customer Name", key: "CustomerName" },
          { label: "Item Name", key: "ItemName" },
          { label: "Item Number", key: "ItemNumber" },
          { label: "Price Per Unit", key: "PricePerUnit" },
          { label: "Quantity", key: "Quantity" },
          { label: "Total Price", key: "TotalPrice" },
        ],
        columnsTicket: [
          { title: '', field: 'TicketNumber' ,
          render: rowData => this.renderTicketEditLink(rowData)
        
        },
          { title: 'Ticket Number', field: 'TicketNumber', defaultSort: 'desc',
          render: rowData => (
            <a
              href="#"
              onClick={() => this.handleLinkClick(rowData)}
            >
              {rowData.TicketNumber}
            </a>
          )
        
        },
          { title: 'PO Number', field: 'PONumber' },
          { title: 'Date', field: 'Date' , render: rowData => moment(rowData.Date).format('MM-DD-YYYY')},
          { title: 'User Name', field: 'UserName' },
          { title: 'Vendor Name', field: 'VendorName' },
          { title: 'Customer Name', field: 'CustomerName' },
          { title: 'Total Ticket Price', field: 'TotalTicketPrice', align: 'right', cellStyle: TABLE_LAST_COL_STYLE, headerStyle: TABLE_LAST_COL_STYLE, render: rowData => <span>${rowData.TotalTicketPrice.toLocaleString()}</span> },
      ],
      columnsTicketReserve: [
        { title: '', field: 'TicketNumber' ,
        render: rowData => (
          this.isReserveOrderCreated(rowData) ? null : (
          <a
            href="#"
            onClick={() => this.handleLinkEditClick(rowData,'return-order')}
          >
            {'Create Order'}
          </a>
          )
        )
      
      },
        { title: '', field: 'TicketNumber' ,
        render: rowData => (
          this.isReserveOrderCreated(rowData) ? null : this.renderTicketEditLink(rowData)
        )
      
      },
        { title: 'Ticket Number', field: 'TicketNumber', defaultSort: 'desc',
        render: rowData => (
          <a
            href="#"
            onClick={() => this.handleLinkClick(rowData)}
          >
            {rowData.TicketNumber}
          </a>
        )
      
      },
        this.reserveOrderTicketColumn,
        { title: 'PO Number', field: 'PONumber' },
        { title: 'Date', field: 'Date' , render: rowData => moment(rowData.Date).format('MM-DD-YYYY')},
        { title: 'User Name', field: 'UserName' },
        { title: 'Vendor Name', field: 'VendorName' },
        { title: 'Customer Name', field: 'CustomerName' },
        { title: 'Total Ticket Price', field: 'TotalTicketPrice', align: 'right', cellStyle: TABLE_LAST_COL_STYLE, headerStyle: TABLE_LAST_COL_STYLE, render: rowData => <span>${rowData.TotalTicketPrice.toLocaleString()}</span> },
    ],
    columnsTicketNonEditableReserve: [
      { title: '', field: 'TicketNumber' ,
      render: rowData => (
        this.isReserveOrderCreated(rowData) ? null : (
        <a
          href="#"
          onClick={() => this.handleLinkEditClick(rowData,'return-order')}
        >
          {'Create Order'}
        </a>
        )
      )
    
    },
      { title: 'Ticket Number', field: 'TicketNumber', defaultSort: 'desc',
      render: rowData => (
        <a
          href="#"
          onClick={() => this.handleLinkClick(rowData)}
        >
          {rowData.TicketNumber}
        </a>
      )
    
    },
      this.reserveOrderTicketColumn,
      { title: 'PO Number', field: 'PONumber' },
      { title: 'Date', field: 'Date' , render: rowData => moment(rowData.Date).format('MM-DD-YYYY')},
      { title: 'User Name', field: 'UserName' },
      { title: 'Vendor Name', field: 'VendorName' },
      { title: 'Customer Name', field: 'CustomerName' },
      { title: 'Total Ticket Price', field: 'TotalTicketPrice', align: 'right', cellStyle: TABLE_LAST_COL_STYLE, headerStyle: TABLE_LAST_COL_STYLE, render: rowData => <span>${rowData.TotalTicketPrice.toLocaleString()}</span> },
  ],
      columnsTicketReturn: [
        { title: '', field: 'TicketNumber' ,
        render: rowData => (
          <a
            href="#"
            onClick={() => this.handleLinkEditClick(rowData,'return')}
          >
            {'Return'}
          </a>
        )
      
      },
        { title: '', field: 'TicketNumber' ,
        render: rowData => this.renderTicketEditLink(rowData)
      
      },
        { title: 'Ticket Number', field: 'TicketNumber', defaultSort: 'desc',
        render: rowData => (
          <a
            href="#"
            onClick={() => this.handleLinkClick(rowData)}
          >
            {rowData.TicketNumber}
          </a>
        )
      
      },
        { title: 'PO Number', field: 'PONumber' },
        { title: 'Date', field: 'Date' , render: rowData => moment(rowData.Date).format('MM-DD-YYYY')},
        { title: 'User Name', field: 'UserName' },
        { title: 'Vendor Name', field: 'VendorName' },
        { title: 'Customer Name', field: 'CustomerName' },
        { title: 'Total Ticket Price', field: 'TotalTicketPrice', align: 'right', cellStyle: TABLE_LAST_COL_STYLE, headerStyle: TABLE_LAST_COL_STYLE, render: rowData => <span>${rowData.TotalTicketPrice.toLocaleString()}</span> },
    ],
    columnsTicketAdjustment: [
      
      { title: '', field: 'TicketNumber' ,
      render: rowData => this.renderTicketEditLink(rowData)
    
    },
      { title: 'Ticket Number', field: 'TicketNumber', defaultSort: 'desc',
      render: rowData => (
        <a
          href="#"
          onClick={() => this.handleLinkClick(rowData)}
        >
          {rowData.TicketNumber}
        </a>
      )
    
    },
      { title: 'PO Number', field: 'PONumber' },
      { title: 'Date', field: 'Date' , render: rowData => moment(rowData.Date).format('MM-DD-YYYY')},
      { title: 'User Name', field: 'UserName' },
      { title: 'Vendor Name', field: 'VendorName' },
      { title: 'Customer Name', field: 'CustomerName' },
      { title: 'Total Ticket Price', field: 'TotalTicketPrice', align: 'right', cellStyle: TABLE_LAST_COL_STYLE, headerStyle: TABLE_LAST_COL_STYLE, render: rowData => <span>${rowData.TotalTicketPrice.toLocaleString()}</span> },
  ]
  ,
    columnsTicketNonEditableAdjustment: [
      
     
      { title: 'Ticket Number', field: 'TicketNumber', defaultSort: 'desc',
      render: rowData => (
        <a
          href="#"
          onClick={() => this.handleLinkClick(rowData)}
        >
          {rowData.TicketNumber}
        </a>
      )
    
    },
      { title: 'PO Number', field: 'PONumber' },
      { title: 'Date', field: 'Date' , render: rowData => moment(rowData.Date).format('MM-DD-YYYY')},
      { title: 'User Name', field: 'UserName' },
      { title: 'Vendor Name', field: 'VendorName' },
      { title: 'Customer Name', field: 'CustomerName' },
      { title: 'Total Ticket Price', field: 'TotalTicketPrice', align: 'right', cellStyle: TABLE_LAST_COL_STYLE, headerStyle: TABLE_LAST_COL_STYLE, render: rowData => <span>${rowData.TotalTicketPrice.toLocaleString()}</span> },
  ]
  ,
      
        columnsTicketNonEditableReturn: [
         
          { title: 'Ticket Number', field: 'TicketNumber', defaultSort: 'desc',
          render: rowData => (
            <a
              href="#"
              onClick={() => this.handleLinkClick(rowData)}
            >
              {rowData.TicketNumber}
            </a>
          )
        
        },
          { title: 'PO Number', field: 'PONumber' },
          { title: 'Date', field: 'Date' , render: rowData => moment(rowData.Date).format('MM-DD-YYYY')},
          { title: 'User Name', field: 'UserName' },
          { title: 'Vendor Name', field: 'VendorName' },
          { title: 'Customer Name', field: 'CustomerName' },
          { title: 'Total Ticket Price', field: 'TotalTicketPrice', align: 'right', cellStyle: TABLE_LAST_COL_STYLE, headerStyle: TABLE_LAST_COL_STYLE, render: rowData => <span>${rowData.TotalTicketPrice.toLocaleString()}</span> },
      ],
      columnsTicketNonEditable: [
        { title: 'Ticket Number', field: 'TicketNumber', defaultSort: 'desc',
        render: rowData => (
          <a
            href="#"
            onClick={() => this.handleLinkClick(rowData)}
          >
            {rowData.TicketNumber}
          </a>
        )
      
      },
        { title: 'PO Number', field: 'PONumber' },
        { title: 'Date', field: 'Date' , render: rowData => moment(rowData.Date).format('MM-DD-YYYY')},
        { title: 'User Name', field: 'UserName' },
        { title: 'Vendor Name', field: 'VendorName' },
        { title: 'Customer Name', field: 'CustomerName' },
        { title: 'Total Ticket Price', field: 'TotalTicketPrice', align: 'right', cellStyle: TABLE_LAST_COL_STYLE, headerStyle: TABLE_LAST_COL_STYLE, render: rowData => <span>${rowData.TotalTicketPrice.toLocaleString()}</span> },
    ],
      headersTicket: [
          { label: "Ticket Number", key: "TicketNumber" },
          { label: "PO Number", key: "PONumber" },
          { label: "Date", key: "Date" },
          { label: "User Name", key: "UserName" },
          { label: "Vendor Name", key: "VendorName" },
          { label: "Customer Name", key: "CustomerName" },
        { label: "Total Ticket Price", key: "TotalTicketPrice" },
      ],
       headersItemTicket: [
         { label: "Ticket Number", key: "TicketNumber" },
        { label: "Date", key: "Date" },
          { label: "Item Number", key: "ItemNumber" },
          { label: "Item Name", key: "ItemName" },

          { label: "Transaction Type", key: "TransactionType" },
          { label: "Vendor/Customer Name", key: "VendorCustomerName" },
          { label: "Qty", key: "Quantity" },
            { label: "Price", key: "PricePerUnit" },
        { label: "Total Price", key: "TotalPrice" },
      ],
       columnsItemLookup: [
        { title: 'Ticket Number', field: 'TicketNumber', defaultSort: 'desc',
        render: rowData => (
          <a
            href="#"
            onClick={() => this.handleLinkClick(rowData)}
          >
            {rowData.TicketNumber}
          </a>
        )
      
      },
        { title: 'Date', field: 'Date' , render: rowData => moment(rowData.Date).format('MM-DD-YYYY')},
        { title: 'Item Number', field: 'ItemNumber' },
        { title: 'Item Name', field: 'ItemName' },
        { title: 'Type of Transaction', field: 'TransactionType' },
        { title: 'Customer / Vendor Name', field: 'VendorCustomerName' },
        { title: 'Qty', field: 'Quantity',align: 'right'  },
        { title: 'Price', field: 'PricePerUnit',align: 'right' , render:  rowData =>  <span>${rowData.PricePerUnit.toLocaleString()}</span> },
        { title: 'Total Ticket Price', field: 'TotalPrice', align: 'right', cellStyle: TABLE_LAST_COL_STYLE, headerStyle: TABLE_LAST_COL_STYLE, render: rowData => <span>${rowData.TotalPrice.toLocaleString()}</span> },
    ],
      showTranDetail:false,
      showTranEdit:false,
      showTranHistory:false,
      invoiceType: 'internal',
      tranHistoryTicket:'',
      tranHistoryRows:[],
      tranHistoryLoading:false,
      returnType:'',
      action:'edit',
      tranDetail:[],
      fullWidth:true,
      maxWidth:'lg',
      maxEditWidth:'lg',
      search:{startDate:null,endDate:null,vendorId:0,customerId:0,ponumber:"",ticketnumber:"",ItemNumber:"",tabindex:0,reserveStatus:'active'},
      apiVendorData:[],
      apiCustomerData:[],
      loading: false,
      fullGroupData:[],
      hasContent:false,
      index:0,
      reserveStatus:'active',
      totalQty:0
       
    }

  // store last MaterialTable query so export can reuse sorting/filtering
  lastQuery = null;

  handleExportClick = () => {
    // Fetch full dataset from server with current filters and sorting
    this.setState({ loading: true });
    let accessToken = getAccessToken();
    const transactionType = (this.state.index == 0)?'OUT':(this.state.index == 1)?'CHK':(this.state.index == 2)?'RET':(this.state.index == 3)?'RES':(this.state.index == 4)?'ADJ':'ItemLookup';

    const q = this.lastQuery || {};
    const params = {
      ponumber:  this.state.search.ponumber ,
      ticketnumber:  this.state.search.ticketnumber ,
      startDate:  moment(this.state.search.startDate).format('MM-DD-YYYY') ,
      endDate:  moment(this.state.search.endDate).format('MM-DD-YYYY') ,
      vendorId:  this.state.search.vendorId ,
      customerId:  this.state.search.customerId ,
      itemNumber:  this.state.search.ItemNumber ,
      transactionType: transactionType,
      reserveStatus: this.state.index === 3 ? this.state.reserveStatus : '',
      type: 'export',
      orderBy: q.orderBy ? q.orderBy.field : '',
      orderDirection: q.orderDirection
    };

    apiClient.get(`/api/get/transaction`,{params:params, headers: {"Authorization" : `Bearer ${accessToken}`} })
      .then(res => {
        const payload = res.data;
        let rows = [];
        if(Array.isArray(payload)) rows = payload;
        else if(payload && payload.rows) rows = payload.rows;
        // optional: transform date fields to a consistent CSV format
        const transformed = rows.map(r => ({ ...r, Date: r.Date ? moment(r.Date).format('MM-DD-YYYY') : ''}));
        this.setState({ exportData: transformed, loading: false });

         if(this.csvLinkRef && this.csvLinkRef.current && this.csvLinkRef.current.link){
             setTimeout(() => {
   this.csvLinkRef.current.link.click();
 }, 1000);
            
          }
      })
      .catch(err => {
        console.error(err);
        this.setState({ loading: false });
      });
  }

  /** Matches DB: only explicit inactive (0/false) counts; null/undefined treated as active. */
  isItemLookupInactive = (option) => {
    if (option == null || typeof option !== 'object') return false;
    const a = option.Active;
    return (
      a === false ||
      a === 0 ||
      String(a).toLowerCase() === 'false' ||
      String(a) === '0'
    );
  };

  getItemLookupOptionLabel = (option) => {
    if (option == null) return '';
    const num = option.ItemNumber != null ? String(option.ItemNumber) : '';
    const name = option.ItemName != null ? String(option.ItemName) : '';
    const base = num && name ? `${num} — ${name}` : num || name;
    if (!base) return '';
    return this.isItemLookupInactive(option) ? `${base} (Inactive)` : base;
  };

  /** Server already filters; keep client pass-through for MUI Autocomplete. */
  filterItemLookupOptions = (options) => options || [];

  fetchItemLookupOptions = (query) => {
    const q = String(query || '').trim();
    if (q.length < 1) {
      this._itemLookupReqId = (this._itemLookupReqId || 0) + 1;
      this.setState({ apiItemData: [], itemLookupLoading: false });
      return;
    }
    let accessToken = getAccessToken();
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      accessToken = user.accessToken || '';
    } catch (e) {
      accessToken = getAccessToken();
    }
    const reqId = (this._itemLookupReqId = (this._itemLookupReqId || 0) + 1);
    this.setState({ itemLookupLoading: true });
    apiClient
      .get(`/api/get/item`, {
        params: {
          forAutocomplete: 1,
          autocompleteEligible: 'all',
          search: q,
          limit: 50,
        },
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        if (reqId !== this._itemLookupReqId) return;
        this.setState({
          apiItemData: Array.isArray(res.data) ? res.data : [],
          itemLookupLoading: false,
        });
      })
      .catch(() => {
        if (reqId !== this._itemLookupReqId) return;
        this.setState({ apiItemData: [], itemLookupLoading: false });
      });
  };

  /** Typing in the field: keep raw text (number or name fragment). Skip 'reset' so we do not store "number — name" in ItemNumber. */
  handleItemLookupInputChange = (event, newInputValue, reason) => {
    if (reason === 'reset') return;
    this.state.search.ItemNumber = newInputValue == null ? '' : String(newInputValue);
    this.state.search.tabindex = this.state.index;
    this.setState({ search: this.state.search });
    sessionStorage.setItem('TranSearch', JSON.stringify(this.state.search));
    if (this._itemLookupTimer) clearTimeout(this._itemLookupTimer);
    if (reason === 'clear') {
      this.fetchItemLookupOptions('');
      return;
    }
    this._itemLookupTimer = setTimeout(() => {
      this.fetchItemLookupOptions(newInputValue);
    }, 300);
  };

  /** Picking from list: persist item number for the transaction search API. */
  handleItemLookupChange = (event, newValue) => {
    if (newValue && typeof newValue === 'object') {
      this.state.search.ItemNumber =
        newValue.ItemNumber != null ? String(newValue.ItemNumber) : '';
    } else {
      this.state.search.ItemNumber = '';
    }
    this.state.search.tabindex = this.state.index;
    this.setState({ search: this.state.search });
    sessionStorage.setItem('TranSearch', JSON.stringify(this.state.search));
  };


    getTicketTransations=(itemDetail,type)=>{
      let user = JSON.parse(sessionStorage.getItem('user'));
			let accessToken = user && user.accessToken ? user.accessToken : '';
			const params = {
				ticketnumber:  itemDetail.TicketNumber ,
			  };

      const authHeaders = { headers: { Authorization: `Bearer ${accessToken}` } };

      const applyTranDetailState = (commissions) => {
        const items = itemDetail.items || [];
        const itemSubtotal = items.reduce(
          (sum, item) => sum + parseTranDetailNumber(item.TotalPrice),
          0
        );
        const totalPaymentFee = items.reduce(
          (sum, item) => sum + parseTranDetailNumber(item.PaymentFee),
          0
        );
        const commissionRows = commissions || [];
        const { total: totalCommission } = sumTranCommissionAmountsByKind(
          commissionRows,
          items
        );

        itemDetail.commissions = commissionRows;
        itemDetail.TotalCommission = totalCommission;
        itemDetail.TotalPaymentFee = totalPaymentFee;
        itemDetail.TotalTicketPrice = applyTranCommissionToTicketTotal(
          itemSubtotal,
          totalPaymentFee,
          commissionRows,
          items
        );

        this.setState({
          tranDetail: itemDetail,
          invoiceType: 'internal',
        });

        if (type === 'Edit') {
          this.setState({ returnType: '' });
          if (itemDetail.TransactionType == 'Return') {
            if (itemDetail.ReturnTicketNumber?.toString().includes('CHK')) {
              this.setState({ returnType: 'CHK' });
            } else {
              this.setState({ returnType: 'OUT' });
            }
          }
          this.setState({ showTranEdit: true });
        } else {
          this.setState({ showTranDetail: true });
        }
      };

			apiClient.get(`/api/get/transactionbyticketnumber`, { params, ...authHeaders })
				.then(res => {
          const rows = res.data || [];
          itemDetail.items = rows;

          // Order Ticket links may pass only TicketNumber — fill header fields from line items.
          if (rows.length > 0) {
            const first = rows[0];
            const fillIfMissing = (key) => {
              if (
                itemDetail[key] == null ||
                itemDetail[key] === '' ||
                itemDetail[key] === undefined
              ) {
                if (first[key] != null && first[key] !== '') {
                  itemDetail[key] = first[key];
                }
              }
            };
            [
              'CustomerName',
              'CustomerId',
              'VendorName',
              'VendorId',
              'UserName',
              'Date',
              'PONumber',
              'TransactionType',
              'ReturnTicketNumber',
              'Broker1CustomerId',
              'Broker2CustomerId',
              'Broker3CustomerId',
              'Broker1Name',
              'Broker2Name',
              'Broker3Name',
            ].forEach(fillIfMissing);
          }

          const isCheckoutInvoice =
            itemDetail.TicketNumber &&
            String(itemDetail.TicketNumber).includes('OUT');

          if (isCheckoutInvoice) {
            apiClient
              .get(`/api/get/invoicecommission`, {
                params: { ticketnumber: itemDetail.TicketNumber },
                ...authHeaders,
              })
              .then((commRes) => {
                applyTranDetailState(mapInvoiceCommissionsFromApi(commRes.data));
              })
              .catch(() => {
                applyTranDetailState([]);
              });
          } else {
            applyTranDetailState([]);
          }
				});
    };
    
    appendTransactionHistoryColumn = (columns) => {
      return [
        ...columns,
        {
          title: 'History',
          field: 'transactionHistory',
          sorting: false,
          filtering: false,
          cellStyle: { whiteSpace: 'nowrap', minWidth: 72 },
          headerStyle: { whiteSpace: 'nowrap', minWidth: 72 },
          render: (rowData) => (
            <a
              href="#"
              className="transaction-history-link"
              onClick={(e) => {
                e.preventDefault();
                this.handleTransactionHistoryClick(rowData);
              }}
            >
              View
            </a>
          ),
        },
      ];
    };

    handleTransactionHistoryClick = (rowData) => {
      const ticketNumber = rowData && rowData.TicketNumber;
      if (!ticketNumber) return;
      this.setState({
        showTranHistory: true,
        tranHistoryLoading: true,
        tranHistoryTicket: ticketNumber,
        tranHistoryRows: [],
      });
      let accessToken = getAccessToken();
      apiClient
        .get('/api/get/transactionHistory', {
          params: { ticketnumber: ticketNumber },
          headers: { Authorization: `Bearer ${accessToken}` },
        })
        .then((res) => {
          const rows = Array.isArray(res.data) ? res.data : [];
          this.setState({ tranHistoryRows: rows, tranHistoryLoading: false });
        })
        .catch((err) => {
          console.error(err);
          this.setState({ tranHistoryLoading: false, tranHistoryRows: [] });
        });
    };

    setShowTranHistory = (open) => {
      this.setState({
        showTranHistory: open,
        ...(open
          ? {}
          : {
              tranHistoryTicket: '',
              tranHistoryRows: [],
              tranHistoryLoading: false,
            }),
      });
    };

    getHistoryActionBadgeClass = (actionDescription) => {
      const a = String(actionDescription || '').toLowerCase();
      if (a.indexOf('created') !== -1) return 'transaction-history-badge transaction-history-badge--created';
      if (a === 'edited') return 'transaction-history-badge transaction-history-badge--edited';
      if (a.indexOf('return') !== -1) return 'transaction-history-badge transaction-history-badge--return';
      return 'transaction-history-badge';
    };

    getHistoryItemActionBadgeClass = (itemAction) => {
      const a = String(itemAction || '').toLowerCase();
      if (a === 'added') return 'transaction-history-item-badge transaction-history-item-badge--added';
      if (a === 'removed') return 'transaction-history-item-badge transaction-history-item-badge--removed';
      if (a === 'modified') return 'transaction-history-item-badge transaction-history-item-badge--modified';
      if (a === 'returned') return 'transaction-history-item-badge transaction-history-item-badge--returned';
      return 'transaction-history-item-badge';
    };

    formatHistoryDate = (value) => {
      if (!value) return '—';
      const m = moment(value);
      return m.isValid() ? m.format('MM-DD-YYYY HH:mm') : String(value);
    };

    handleLinkClick = (itemDetail) => {
      
      this.getTicketTransations(itemDetail,'Detail');
    };

    handleLinkEditClick = (itemDetail,action) => {
        if (action === 'edit' && !this.isTicketEditable(itemDetail)) return;
     
        this.setState({ action: action });
        
        this.getTicketTransations(itemDetail,'Edit');
    };


getTransactionType = (TicketNumber) =>{

if(TicketNumber != undefined)
{
  if(TicketNumber.includes('OUT'))
  {
    return 'Invoice';
  }
  else if(TicketNumber.includes('CHK'))
  {
    return  'Purchase Order';
  }
  else if(TicketNumber.includes('RET'))
  {
    return  'Product Return';
  }
  else if(TicketNumber.includes('RES'))
  {
    return 'Product Reserves';
  }
  else if(TicketNumber.includes('ADJ'))
  {
    return 'Product Adjustments';
  }
}else{
  return '';
}
 

};


setShowTranDetail = (date) => {
   this.setState({ showTranDetail: false, invoiceType: 'internal' });
  
};

handleChildChange = () => {
  this.setShowTranEdit(false);
  const { history } = this.props;
  
  this.state.search["startDate"] = null;
  this.state.search["endDate"] = null;
  this.state.search["vendorId"] = 0;
  this.state.search["customerId"] = 0;
  this.state.search["ponumber"] = "";
  this.state.search["ticketnumber"] = "";
  this.state.search["ItemNumber"] = "";
  this.state.search["tabindex"] = this.state.index;
  
  this.setState({
    search:  this.state.search
   });
   sessionStorage.setItem('TranSearch', JSON.stringify(this.state.search));
  history.push('/vendor-details');
  setTimeout(() => {
   history.push('/transactions');
 }, 1);

  };

setShowTranEdit = (date,action) => {
  this.setState({ action: action });
 
  this.setState({ showTranEdit: false });
 
};

	// Non-inventory items should not contribute to Purchase Order Total Quantity.
	isNonInventoryRow = (row) => {
		const rowItemId = row?.itemid ?? row?.ItemId;
		if (rowItemId != null && rowItemId !== '') {
			const matchedItem = (this.state.apiItemData || []).find(
				(item) => Number(item?.ItemId) === Number(rowItemId)
			);
			if (matchedItem) {
				const itemFlag = matchedItem.IsNonInventory;
				return (
					itemFlag === true ||
					itemFlag === 1 ||
					String(itemFlag).toLowerCase() === 'true'
				);
			}
		}
		const v = row?.isNonInventory ?? row?.IsNonInventory ?? row?.isNoninventory;
		return v === true || v === 1 || String(v).toLowerCase() === 'true';
	};

getTotalQuantity= () =>{
 var totalQty = 0;
  this.state.tranDetail.items.forEach(element => {
    if(!this.isNonInventoryRow(element)) {
					totalQty += this.toQty(element?.Quantity);
				}
  });
return totalQty;
};

getTotalAmount= () =>{
 var totalAmt = 0;
  this.state.tranDetail.items.forEach(element => {
    totalAmt += element.TotalPrice;
  });
return totalAmt;
};

getTotalPaymentFee= () =>{
 var totalAmt = 0;
  this.state.tranDetail.items.forEach(element => {
    totalAmt += parseTranDetailNumber(element.PaymentFee);
  });
return totalAmt;
};

isCheckoutInvoiceTicket = (ticketNumber) => {
  const tn = ticketNumber ?? this.state.tranDetail?.TicketNumber;
  return !!(tn && String(tn).includes('OUT'));
};

isInternalInvoiceAccounting = () =>
  this.isCheckoutInvoiceTicket() && this.state.invoiceType === 'internal';

getItemCategoryName = (item) => {
  if (item?.CategoryName) return item.CategoryName;
  const itemId = item?.ItemId ?? item?.itemid;
  const itemNumber = item?.ItemNumber ?? item?.itemnumber;
  const catalog = this.state.apiItemData || [];
  if (itemId != null && itemId !== '') {
    const matched = catalog.find((x) => Number(x?.ItemId) === Number(itemId));
    if (matched?.CategoryName) return matched.CategoryName;
  }
  if (itemNumber != null && String(itemNumber).trim() !== '') {
    const matched = catalog.find(
      (x) => String(x?.ItemNumber) === String(itemNumber)
    );
    if (matched?.CategoryName) return matched.CategoryName;
  }
  return '';
};

getItemEntityGroupName = (item) => {
  if (item?.EntityGroupName) return item.EntityGroupName;
  if (item?.entityGroupName) return item.entityGroupName;
  const itemId = item?.ItemId ?? item?.itemid;
  const itemNumber = item?.ItemNumber ?? item?.itemnumber;
  const catalog = this.state.apiItemData || [];
  if (itemId != null && itemId !== '') {
    const matched = catalog.find((x) => Number(x?.ItemId) === Number(itemId));
    if (matched?.EntityGroupName) return matched.EntityGroupName;
  }
  if (itemNumber != null && String(itemNumber).trim() !== '') {
    const matched = catalog.find((x) => String(x?.ItemNumber) === String(itemNumber));
    if (matched?.EntityGroupName) return matched.EntityGroupName;
  }
  return '';
};

isCommissionCategoryItem = (item) => {
  const categoryName = String(this.getItemCategoryName(item) || '')
    .trim()
    .toLowerCase();
  return categoryName === 'commission' || categoryName === 'commision';
};

getTranDetailDisplayItems = () => {
  const items = this.state.tranDetail?.items || [];
  if (!this.isCheckoutInvoiceTicket() || this.state.invoiceType !== 'client') {
    return items;
  }
  // Client invoice: hide commission lines; bake Rate/Individual into unit prices.
  const invoiceItems = items.filter((item) => !this.isCommissionCategoryItem(item));
  const commissions = this.state.tranDetail?.commissions || [];
  let { fixedTotal, profitTotal } = sumTranCommissionAmountsByKind(
    commissions,
    invoiceItems
  );
  if (fixedTotal <= 0 && profitTotal <= 0) {
    const rawTotal = parseTranDetailNumber(this.state.tranDetail?.TotalCommission);
    if (rawTotal > 0) fixedTotal = rawTotal;
  }
  return distributeFixedCommissionIntoTransactionItems(invoiceItems, fixedTotal);
};

getTranDetailDisplayTotals = () => {
  const td = this.state.tranDetail || {};
  const sourceItems = (td.items || []).filter(
    (item) => !this.isCommissionCategoryItem(item)
  );
  const originalSubtotal = sourceItems.reduce(
    (sum, item) => sum + getTranItemTotal(item),
    0
  );
  const paymentFee = parseTranDetailNumber(td.TotalPaymentFee);
  const commissions = td.commissions || [];
  let { total: commission, fixedTotal, profitTotal } =
    sumTranCommissionAmountsByKind(commissions, sourceItems);
  if (fixedTotal <= 0 && profitTotal <= 0) {
    const rawTotal = parseTranDetailNumber(td.TotalCommission);
    if (rawTotal > 0) {
      fixedTotal = rawTotal;
      commission = rawTotal;
    }
  }

  // Rate/Individual: Internal and Client grand totals both use
  // originalSubtotal + paymentFee + fixedTotal (profit omitted on client).
  const sharedGrandTotal =
    originalSubtotal +
    paymentFee -
    (this.state.invoiceType === 'client' ? 0 : profitTotal) +
    fixedTotal;

  if (this.isCheckoutInvoiceTicket() && this.state.invoiceType === 'client') {
    const displayItems = this.getTranDetailDisplayItems();
    const displaySubtotal = displayItems.reduce(
      (sum, item) => sum + getTranItemTotal(item),
      0
    );
    return {
      itemSubtotal: displaySubtotal,
      totalCommission: 0,
      totalTicketPrice: sharedGrandTotal,
      totalPaymentFee: paymentFee,
      fixedCommissionTotal: fixedTotal,
    };
  }

  return {
    itemSubtotal: originalSubtotal,
    totalCommission: commission || parseTranDetailNumber(td.TotalCommission),
    totalTicketPrice: sharedGrandTotal,
    totalPaymentFee: paymentFee,
    fixedCommissionTotal: fixedTotal,
  };
};

getTranDetailDisplayQuantity = () => {
  let totalQty = 0;
  this.getTranDetailDisplayItems().forEach((element) => {
    if (!this.isNonInventoryRow(element)) {
      totalQty += getTranItemQty(element);
    }
  });
  return totalQty;
};

handleInvoiceTypeChange = (e) => {
  this.setState({ invoiceType: e.target.value });
};



   

    handleSetSearchDate = (value,type) => {
      

      if(type=='start')
      {
        this.state.search["startDate"] = value;
        this.state.search["tabindex"] = this.state.index;
        this.setState({
          search: this.state.search,
         });

         
      }else{
        this.state.search["endDate"] = value;
        this.state.search["tabindex"] = this.state.index;
        this.setState({
          search: this.state.search,
         });
      }
      sessionStorage.setItem('TranSearch', JSON.stringify(this.state.search));
     
    };
   
    handleSetSearchData = (event) => {
      this.state.search[event.target.name] = event.target.value;
      this.state.search["tabindex"] = this.state.index;
      this.setState({
        search: this.state.search,
       });
       sessionStorage.setItem('TranSearch', JSON.stringify(this.state.search));
    };

    componentDidMount() {
      this._editWindowTimer = setInterval(() => {
        this.setState({ editWindowNow: Date.now() });
      }, 60000);
      let user = JSON.parse(sessionStorage.getItem('user'));
      let accessToken = getAccessToken();

      apiClient.get(`/api/get/vendor`, { headers: {"Authorization" : `Bearer ${accessToken}`} })
		  .then(res => {
			const vendorList = res.data;
			this.setState({apiVendorData:vendorList});
		  });

      apiClient.get(`/api/get/customer`, { headers: {"Authorization" : `Bearer ${accessToken}`} })
		  .then(res => {
			const customerList = res.data;
			this.setState({apiCustomerData:customerList});
      
		  });
  
		  


     
      let TranSearch = JSON.parse(sessionStorage.getItem('TranSearch'));
      if (TranSearch != null) {
        const rawTab = TranSearch.tabindex;
        const index = normalizeTransactionTabIndex(
          typeof rawTab === 'number' ? rawTab : parseInt(rawTab, 10) || 0,
          user
        );
        const reserveStatus = TranSearch.reserveStatus === 'inactive' ? 'inactive' : 'active';
        TranSearch = { ...TranSearch, tabindex: index, reserveStatus };
        this.setState({ search: TranSearch, index, reserveStatus });
      }
      setTimeout(() => {
        if(this.state.index == 0){
          this.handleSearch("OUT");
        }
        else if(this.state.index == 1){
            this.handleSearch("CHK");
        }
        else if(this.state.index == 2){
          this.handleSearch("RET");
      }
      else if(this.state.index == 3){
        this.handleSearch("RES");
    }
    else if(this.state.index == 4){
      this.handleSearch("ADJ");
      
  }
  else if(this.state.index == 5){
      this.handleSearch("ItemLookup");
      
  }
      }, 1); 

     
    };
    componentWillUnmount() {
      if (this._itemLookupTimer) clearTimeout(this._itemLookupTimer);
      if (this._editWindowTimer) clearInterval(this._editWindowTimer);
      this._itemLookupReqId = (this._itemLookupReqId || 0) + 1;
    }
    a11yProps(index) {
      return {
        id: `scrollable-auto-tab-${index}`,
        'aria-controls': `scrollable-auto-tabpanel-${index}`,
      };
    }
      

    onTabClicked = (event, newValue) => {
      const { history } = this.props;
      this.setState({ index: newValue });
      this.state.search["startDate"] = null;
      this.state.search["endDate"] = null;
      this.state.search["vendorId"] = 0;
      this.state.search["customerId"] = 0;
      this.state.search["ponumber"] = "";
      this.state.search["ticketnumber"] = "";
      this.state.search["ItemNumber"] = "";
      this.state.search["tabindex"] = newValue;
      this.state.search["reserveStatus"] = "active";
      
      this.setState({
        search:  this.state.search,
        reserveStatus: 'active'
       });
       sessionStorage.setItem('TranSearch', JSON.stringify(this.state.search));
      history.push('/vendor-details');
         setTimeout(() => {
          history.push('/transactions');
        }, 1);

let user = JSON.parse(sessionStorage.getItem('user'));
         if(user.permissions.filter(x=>x.PermissionName == 'Transaction' && x.PermissionAllowed == 1).length == 0)
         {
            const { history } = this.props;
            history.push('/home');
         }
     
      
     
  }
    
    handleMoreSearch = () => {

      const { history } = this.props;
      history.push('/vendor-details');
         setTimeout(() => {
          history.push('/transactions');
        }, 1); 
    };


    handleSearch = (search) => {
      // For server-side paging we simply (re)render the table which will fetch pages on demand.
      this.setState({ loading: true });
      // ensure search.transactionType is in sync (used by renderTable)
      this.state.search.tabindex = this.state.index;
      this.setState({ search: this.state.search }, () => {
        this.renderTable();
        this.setState({ loading: false });
      });
    };

    handleReserveStatusChange = (event) => {
      const reserveStatus = event.target.value;
      const search = { ...this.state.search, reserveStatus };
      sessionStorage.setItem('TranSearch', JSON.stringify(search));
      this.setState({ reserveStatus, search }, () => this.renderTable());
    };

    

    generatePdf = () => {
      exportInvoicePdf({
        element: this.contentRef.current,
        gridElement: this.gridRef.current,
        hiddenElements: [this.invoiceTypeToggleRef.current],
      });

    };

   

    fetchData = () => {
      

        return (dispatch) => {
      let accessToken = getAccessToken();
      const params = {
				ponumber:  this.state.search.ponumber ,
        ticketnumber:  this.state.search.ticketnumber ,
        startDate:  moment(this.state.search.startDate).format('MM-DD-YYYY') ,
        endDate:  moment(this.state.search.endDate).format('MM-DD-YYYY') ,
        vendorId:  this.state.search.vendorId ,
        customerId:  this.state.search.customerId ,
			  };
          apiClient.get(`/api/get/transaction`,{params:params, headers: {"Authorization" : `Bearer ${accessToken}`} })
            .then(response => {
              dispatch({ type: 'FETCH_DATA_SUCCESS', payload: response.data });
            })
            .catch(error => {
              dispatch({ type: 'FETCH_DATA_FAILURE', payload: error });
            });
        };
    };

      
    applyQuery = (query, data) => {
      let filteredData = [...data];
    
      // Apply search filter
      if (query.search) {
        filteredData = filteredData.filter(
          row =>
            row.name.toLowerCase().includes(query.search.toLowerCase()) ||
            row.age.toString().includes(query.search)
        );
      }
    
      // Apply sorting
      if (query.orderBy) {
        const field = query.orderBy.field;
        const direction = query.orderDirection === 'desc' ? -1 : 1;
        filteredData.sort((a, b) => {
          if (a[field] < b[field]) return -direction;
          if (a[field] > b[field]) return direction;
          return 0;
        });
      }
    
      // Apply paging
      if (query.pageSize) {
        filteredData = filteredData.slice(
          query.page * query.pageSize,
          query.page * query.pageSize + query.pageSize
        );
      }
    
      return filteredData;
    };

    renderTable = () => {
      const user = JSON.parse(sessionStorage.getItem('user'));
      const baseColumns = this.state.index== 5?this.state.columnsItemLookup: this.state.index== 4?( user.permissions.filter(x=>x.PermissionName == 'Edit Transaction' && x.PermissionAllowed == 1).length > 0 ? this.state.columnsTicketAdjustment: this.state.columnsTicketNonEditableAdjustment):this.state.index == 3? ( user.permissions.filter(x=>x.PermissionName == 'Edit Transaction' && x.PermissionAllowed == 1).length > 0 ? this.state.columnsTicketReserve: this.state.columnsTicketNonEditableReserve):this.state.index == 2? ( user.permissions.filter(x=>x.PermissionName == 'Edit Transaction' && x.PermissionAllowed == 1).length > 0 ? this.state.columnsTicket: this.state.columnsTicketNonEditable):( user.permissions.filter(x=>x.PermissionName == 'Edit Transaction' && x.PermissionAllowed == 1).length > 0 ? this.state.columnsTicketReturn: this.state.columnsTicketNonEditableReturn);
      const columns = this.appendTransactionHistoryColumn(baseColumns);
      const title = this.state.index == 0?'Invoices List':this.state.index == 1?'Purchase Orders List':this.state.index == 2?'Returns List':this.state.index == 3?'Reserves List':this.state.index == 4?'Adjustments List':'Item Lookup List';

      ReactDOM.render(
        <MaterialTable
          key={`transactions-${this.state.index}-${this.state.reserveStatus}`}
          columns={columns}
          title={title}
            data={query => new Promise((resolve, reject) => {
            // save last query for export use
            this.lastQuery = query;
            // Server-side paging: forward MaterialTable query params to backend
            let accessToken = getAccessToken();
            const transactionType = (this.state.index == 0)?'OUT':(this.state.index == 1)?'CHK':(this.state.index == 2)?'RET':(this.state.index == 3)?'RES':(this.state.index == 4)?'ADJ':'ItemLookup';
            const params = {
              ponumber:  this.state.search.ponumber ,
              ticketnumber:  this.state.search.ticketnumber ,
              startDate:  moment(this.state.search.startDate).format('MM-DD-YYYY') ,
              endDate:  moment(this.state.search.endDate).format('MM-DD-YYYY') ,
              vendorId:  this.state.search.vendorId ,
              customerId:  this.state.search.customerId ,
              itemNumber:  this.state.search.ItemNumber ,
              transactionType: transactionType,
              reserveStatus: this.state.index === 3 ? this.state.reserveStatus : '',
              page: query.page,
              pageSize: query.pageSize,
              orderBy: query.orderBy ? query.orderBy.field : '',
              orderDirection: query.orderDirection
            };

            apiClient.get(`/api/get/transaction`,{params:params, headers: {"Authorization" : `Bearer ${accessToken}`} })
              .then(res => {
                // Backend returns { rows, totalCount } for paged endpoints, but keep backwards-compat support
                const payload = res.data;
                let rows = [];
                let totalCount = 0;
                if(Array.isArray(payload)){
                  rows = payload;
                  totalCount = payload.length;
                } else if(payload && payload.rows){
                  rows = payload.rows;
                  totalCount = payload.totalCount || (rows ? rows.length : 0);
                }

                resolve({ data: rows, page: query.page, totalCount: totalCount });
              })
              .catch(err => {
                console.error(err);
                resolve({ data: [], page: query.page, totalCount: 0 });
              })
          })}
          options={{
            sorting: true,
            search: false,
            paging: true,
            padding: 'dense',
            pageSize: 10,
            pageSizeOptions: [10, 20, 50],
            headerStyle: {
              fontSize: '0.8125rem',
              fontWeight: 600,
              padding: '8px 12px',
            },
            rowStyle: {
              fontSize: '0.8125rem',
            },
          }}
        />,
        document.getElementById('data-table-container')
      );
    };
    
  render() {
    let user = JSON.parse(sessionStorage.getItem('user'));
    const { classes } = this.props;

    return (
      <Box className='Tab' >
                    <AppBar position="static">
                        <Tabs value={this.state.index} onChange={this.onTabClicked}>
                        <Tab label="Invoice" value={0} {...this.a11yProps(0)} />
                        <Tab label="Purchase Order" value={1} {...this.a11yProps(1)}/>
                        <Tab label="Product Return" value={2} {...this.a11yProps(2)}/>
                        <Tab label="Product Reserves" value={3} {...this.a11yProps(3)}/>
                        {isAdjustmentTabVisible(user) && (
                          <Tab label="Product Adjustments" value={4} {...this.a11yProps(4)} />
                        )}
                        {userHasLookupPermission(user) && (
                          <Tab label="Item Lookup" value={5} {...this.a11yProps(5)} />
                        )}
                        
                        </Tabs>
                    </AppBar>
      <div style={{ maxWidth: '100%' }} className="Transaction">
         <form   style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
         
          <div >
                  <FormControl  margin="normal">
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DatePicker
                      label="Start Order Date"
                      name="startDate"
                      format="MM/dd/yyyy"
                      value={this.state.search.startDate}
                      onChange={(e) => this.handleSetSearchDate(e,'start')}
                      displayEmpty
                      margin="normal"
                      clearable
                    />
                    </MuiPickersUtilsProvider>
                  </FormControl>
          </div>
          <div  >
                  <FormControl  margin="normal">
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DatePicker
                      name="endDate"
                      label="End Order Date"
                      format="MM/dd/yyyy"
                      value={this.state.search.endDate}
                      onChange={(e) => this.handleSetSearchDate(e,'end')}
                      displayEmpty
                      margin="normal"
                      clearable
                    />
                    </MuiPickersUtilsProvider>
                  </FormControl>
          </div>
          <div  >
                <FormControl  margin="normal">
                <InputLabel id="select-label">Vendor</InputLabel>
                <Select
                              label="Vendor"
                  name="vendorId" 
                              value={this.state.search.vendorId}
                  onChange={(e) => this.handleSetSearchData(e)}

                  margin="normal"
                              displayEmpty
                              inputProps={{ 'aria-label': 'Without label' }} >
                             <MenuItem value='0'>  </MenuItem>
                              {this.state.apiVendorData.map((item) => (
                              <MenuItem value={item.VendorId}>  {item.VendorName}</MenuItem>
                              ))}
                  </Select>  
              </FormControl>
          </div>     
          <div >
                <FormControl  margin="normal">
                <InputLabel id="select-label">Select Customer</InputLabel>
                <Select
                              label="Customer"
                  name="customerId" 
                              value={this.state.search.customerId}
                  onChange={(e) => this.handleSetSearchData(e)}

                  margin="normal"
                              displayEmpty
                              inputProps={{ 'aria-label': 'Without label' }} >
                             <MenuItem  value='0' >  </MenuItem>
                              {this.state.apiCustomerData.map((item) => (
                              <MenuItem value={item.CustomerId}>  {item.CustomerName}</MenuItem>
                              ))}
                  </Select>  
              </FormControl>
          </div>
         

        <div style={{ display: this.state.index != 5 ? 'block' : 'none' }}>
                            <TextField 
                            style= {{ backgroundColor: 'transparent' }}
                          label="PO Number"
                      className={classes.customTextField}
                          variant="filled"
                    
                      InputLabelProps={{
                        style: { textAlign: 'left', backgroundColor: 'transparent' },
                        }}
                          name="ponumber"
                          value={this.state.search.ponumber}
                          onChange={(e) => this.handleSetSearchData(e)}
                        />
                  </div>

        <div  style={{ display: this.state.index != 5 ? 'block' : 'none' }}>
                            <TextField 
                            style= {{ backgroundColor: 'transparent' }}
                          label="Ticket Number"
                      className={classes.customTextField}
                          variant="filled"
                    
                      InputLabelProps={{
                        style: { textAlign: 'left', backgroundColor: 'transparent' },
                        }}
                          name="ticketnumber"
                          value={this.state.search.ticketnumber}
                          onChange={(e) => this.handleSetSearchData(e)}
                        />
                  </div>

          <div style={{ display: this.state.index == 5 ? 'block' : 'none' }}>
                  
                        <InputLabel id="select-label">Item number or name</InputLabel>
                          <Autocomplete
                              id="itemid"
                              name="itemid"
                              inputValue={this.state.search.ItemNumber}
                              options={this.state.apiItemData}
                              loading={this.state.itemLookupLoading}
                              getOptionLabel={(option) => this.getItemLookupOptionLabel(option)}
                              filterOptions={this.filterItemLookupOptions}
                              onInputChange={this.handleItemLookupInputChange}
                              onChange={this.handleItemLookupChange}
                              onOpen={() => {
                                const q = this.state.search?.ItemNumber;
                                if (String(q || '').trim().length >= 1) {
                                  this.fetchItemLookupOptions(q);
                                }
                              }}
                              style={{ width: 320, border: null }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="standard"
                                  placeholder="Type number or name…"
                                />
                              )}
                              />
                      
                  </div>
         
          <div style={{textAlign:'left', paddingBottom: '3px',marginTop:'5px'}}>
          
            
       <Button
                    color="primary"
                    style= {{ width: '250px' , textAlign:'left'}}
                    variant="contained"
                    onClick={() => this.handleMoreSearch()}
                    disabled={this.submitted}
                >
                    {
                         'Search'
                    }
                </Button>

     
            </div>
         
        
                <br/>
                <br/>

         </form>
        
        
        <br/>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '3px' }}>
          <div>
            {this.state.index === 3 && (
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  aria-label="Reserve ticket status"
                  name="reserveStatus"
                  value={this.state.reserveStatus}
                  onChange={this.handleReserveStatusChange}
                >
                  <FormControlLabel value="active" control={<Radio color="primary" />} label="Active" />
                  <FormControlLabel value="inactive" control={<Radio color="primary" />} label="Inactive" />
                </RadioGroup>
              </FormControl>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
          <a
            href='#'
            style= {{ width: '250px' , textAlign:'left'}}
            variant="contained"
            onClick={() => this.handleExportClick()}
            disabled={this.state.loading}
          >
            Export to Excel
          </a>
          {/* Hidden CSVLink used to trigger download after data is fetched */}
          <CSVLink
            data={this.state.exportData}
            headers={this.state.index == 5 ? this.state.headersItemTicket:this.state.headersTicket}
            filename={`transactions-${moment().format('YYYYMMDD-HHmmss')}.csv`}
            ref={this.csvLinkRef}
            style={{ display: 'none' }}
          />
          </div>
        </div>

        <div id="data-table-container">

        </div>
        
       
{this.state.showTranEdit && ( 
					<div> 
						<Dialog  
						  className="transaction-edit-dialog"
						  fullWidth={this.state.fullWidth}
						  maxWidth={this.state.maxEditWidth}
						open={this.state.showTranEdit} 
						onClose={() => this.setShowTranEdit(false)}
						disableEnforceFocus
						> 
            <DialogTitle id="alert-dialog-title"> 
						
						<div style={{float:"right"}}>
						<IconButton
								aria-label="close"
								onClick={() => this.setShowTranEdit(false)}
								sx={{
									position: "absolute",
									right: 8,
									top: 5,
									color: (theme) => theme.palette.grey[500]
								}}
								>
								<CloseIcon />
								</IconButton>
						</div>
							
							
						</DialogTitle> 
					
						<DialogContent className="transaction-dialog-content" id="alert-dialog-description"> 
							<div className="transaction-edit-dialog-body"> 
					{
            (this.state.index == 1 || this.state.index == 2)?(
          		(this.state.returnType =='CHK' || this.state.returnType =='')?(<CheckIn embeddedInDialog tranDetail={this.state.tranDetail} action={this.state.action} onSendValue={() => this.handleChildChange()} />)
              :
              (
              <CheckOut embeddedInDialog tranDetail={this.state.tranDetail} action={this.state.action} onSendValue={() => this.handleChildChange()} />

              ))
              :(
              <CheckOut embeddedInDialog tranDetail={this.state.tranDetail} action={this.state.action} onSendValue={() => this.handleChildChange()} />

              )}
          
							</div> 
						</DialogContent> 
						<DialogActions> 
							
							
						</DialogActions> 
						</Dialog> 
            
					</div> 
					)} 

{this.state.showTranHistory && (
          <Dialog
            className="transaction-history-dialog"
            fullWidth={this.state.fullWidth}
            maxWidth="md"
            open={this.state.showTranHistory}
            onClose={() => this.setShowTranHistory(false)}
          >
            <DialogTitle disableTypography className="transaction-history-dialog__title">
              <div className="transaction-history-dialog__title-row">
                <div>
                  <Typography variant="h6" component="h2">
                    History
                  </Typography>
                  <Typography variant="body2" color="textSecondary" className="transaction-history-dialog__subtitle">
                    {this.state.tranHistoryTicket}
                    {this.state.tranHistoryTicket
                      ? ` · ${this.getTransactionType(this.state.tranHistoryTicket)}`
                      : ''}
                  </Typography>
                </div>
                <IconButton aria-label="close" onClick={() => this.setShowTranHistory(false)}>
                  <CloseIcon />
                </IconButton>
              </div>
            </DialogTitle>
            <DialogContent dividers className="transaction-history-dialog__content">
              {this.state.tranHistoryLoading ? (
                <div className="transaction-history-dialog__loading">
                  <Spinner />
                </div>
              ) : this.state.tranHistoryRows.length === 0 ? (
                <Typography variant="body2" color="textSecondary" className="transaction-history-dialog__empty">
                  No history recorded for this ticket yet.
                </Typography>
              ) : (
                <TableContainer className="transaction-history-table-wrap">
                  <Table size="small" className="transaction-history-table" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date &amp; Time</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Item</TableCell>
                        <TableCell>Line</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {this.state.tranHistoryRows.map((row) => (
                        <TableRow key={row.TransactionHistoryId}>
                          <TableCell className="transaction-history-table__date">
                            {this.formatHistoryDate(row.ActionDate)}
                          </TableCell>
                          <TableCell className="transaction-history-table__user">
                            <span className="transaction-history-table__user-name">
                              {row.UserName || `User #${row.UserId}`}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={this.getHistoryActionBadgeClass(row.ActionDescription)}>
                              {row.ActionDescription || '—'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {row.ItemNumber || row.ItemName ? (
                              <div className="transaction-history-table__item">
                                {row.ItemNumber && (
                                  <span className="transaction-history-table__item-number">{row.ItemNumber}</span>
                                )}
                                {row.ItemName && (
                                  <span className="transaction-history-table__item-name">{row.ItemName}</span>
                                )}
                              </div>
                            ) : row.ItemId ? (
                              <span className="transaction-history-table__item-id">ID {row.ItemId}</span>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>
                            {row.ItemAction ? (
                              <span className={this.getHistoryItemActionBadgeClass(row.ItemAction)}>
                                {row.ItemAction}
                              </span>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell className="transaction-history-table__details">
                            {row.ItemDetails || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.setShowTranHistory(false)} color="primary">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        )}

{this.state.showTranDetail && ( 
					<div> 
						<Dialog  
						  className="transaction-detail-dialog"
						  fullWidth={this.state.fullWidth}
						  maxWidth={this.state.maxWidth}
						open={this.state.showTranDetail} 
						onClose={() => this.setShowTranDetail(false)} 
						> 
            <DialogTitle id="alert-dialog-title"> 
						<div style={{float:"left"}}>
            <IconButton
								aria-label="download"
								onClick={() => this.generatePdf()}
								sx={{
									position: "absolute",
									right: 8,
									top: 5,
									color: (theme) => theme.palette.grey[500]
								}}
								>
								 <CloudDownloadIcon />
								</IconButton>
					
						</div>
						<div style={{float:"right"}}>
						<IconButton
								aria-label="close"
								onClick={() => this.setShowTranDetail(false)}
								sx={{
									position: "absolute",
									right: 8,
									top: 5,
									color: (theme) => theme.palette.grey[500]
								}}
								>
								<CloseIcon />
								</IconButton>
						</div>
							
							
						</DialogTitle> 
					
						<DialogContent className="transaction-dialog-content" ref={this.contentRef} id="alert-dialog-description"> 
             <h2 className="transaction-detail-title">Transaction Detail</h2>
             {this.isCheckoutInvoiceTicket() && (
               <div
                 ref={this.invoiceTypeToggleRef}
                 style={{ display: 'flex', justifyContent: 'center', margin: '8px 0 12px' }}
               >
                 <RadioGroup
                   row
                   value={this.state.invoiceType}
                   onChange={this.handleInvoiceTypeChange}
                 >
                   <FormControlLabel
                     value="internal"
                     control={<Radio color="primary" />}
                     label="Internal Invoice"
                   />
                   <FormControlLabel
                     value="client"
                     control={<Radio color="primary" />}
                     label="Client Invoice"
                   />
                 </RadioGroup>
               </div>
             )}
             <TransactionHeader
               ticketNumber={this.state.tranDetail.TicketNumber}
               recipientName={
                 this.state.tranDetail.CustomerName || this.state.tranDetail.VendorName
               }
               date={moment(this.state.tranDetail.Date).format('MM-DD-YYYY')}
               userName={this.state.tranDetail.UserName}
               showBrokers={
                 this.isCheckoutInvoiceTicket() && this.state.invoiceType === 'internal'
               }
               brokers={[1, 2, 3].map((brokerNumber) => ({
                 number: brokerNumber,
                 name: this.state.tranDetail[`Broker${brokerNumber}Name`],
               }))}
             />

          <div ref={this.gridRef} className="transaction-detail-grid-wrap">
                  <Table size="small" className="transaction-detail-table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item Number</TableCell>
                        <TableCell>Item Name</TableCell>
	                        <TableCell align="right">Quantity</TableCell>
	                        <TableCell align="right">{this.isInternalInvoiceAccounting() ? 'Sales Price' : 'Price'}</TableCell>
	                        <TableCell align="right">{this.isInternalInvoiceAccounting() ? 'Total Sales' : 'Total'}</TableCell>
	                        {this.isInternalInvoiceAccounting() && (
	                          <>
	                            <TableCell align="right">Cost</TableCell>
	                            <TableCell align="right">CP Commission</TableCell>
	                            <TableCell align="right">QB Total</TableCell>
	                          </>
	                        )}
	                      </TableRow>
                    </TableHead>
					<TableBody>
					  <InvoiceLineRows
						items={this.getTranDetailDisplayItems()}
						grouped={this.isCheckoutInvoiceTicket() && this.state.invoiceType === 'internal'}
						getEntityGroupName={this.getItemEntityGroupName}
						getItemNumber={(item) => item.ItemNumber}
						getItemName={(item) => item.ItemName}
						getQuantity={(item) => this.toQty(item?.Quantity)}
							getUnitPrice={(item) => parseTranDetailNumber(item.PricePerUnit)}
							getTotalPrice={(item) => parseTranDetailNumber(item.TotalPrice)}
							getUnitCost={(item) => parseTranDetailNumber(item.Cost)}
							getRowKey={(item, idx) => item.ItemId || item.ItemNumber || idx}
							showAccountingColumns={this.isInternalInvoiceAccounting()}
							commissions={(this.state.tranDetail && this.state.tranDetail.commissions) || []}
						  />
					</TableBody>
                    <TableFooter>
	                      {this.isCheckoutInvoiceTicket() &&
	                        !this.isInternalInvoiceAccounting() &&
	                        (((this.state.tranDetail?.commissions || []).length > 0) ||
                          parseTranDetailNumber(this.state.tranDetail?.TotalCommission) > 0) && (
                      <TableRow>
                        <TableCell colSpan={3} />
                        <TableCell align="right"><b>Subtotal:</b></TableCell>
                        <TableCell align="right"><b>${(this.getTranDetailDisplayTotals().itemSubtotal || 0).toLocaleString()}</b></TableCell>
                      </TableRow>
                      )}
	                    <TableRow>
	                        <TableCell colSpan={this.isInternalInvoiceAccounting() ? 6 : 3} />
	                        <TableCell align="right"><b>Total Quantity:</b></TableCell>
                        <TableCell align="right"><b>{this.getTranDetailDisplayQuantity()?.toLocaleString()}</b></TableCell>
                      </TableRow>
                      {SHOW_PAYMENT_FEE && this.isCheckoutInvoiceTicket() && (
	                                  <TableRow>
	                                              <TableCell colSpan={this.isInternalInvoiceAccounting() ? 6 : 3} />
	                                              <TableCell align="right"><b>Payment Fee:</b></TableCell>
                                              <TableCell align="right"><b>${this.getTranDetailDisplayTotals().totalPaymentFee.toLocaleString()}</b></TableCell>
                                            </TableRow>
                                )}
	                      <TableRow>
	                        <TableCell colSpan={this.isInternalInvoiceAccounting() ? 6 : 3} />
                        <TableCell align="right"><b>Grand Total:</b></TableCell>
                        <TableCell align="right"><b>${this.getTranDetailDisplayTotals().totalTicketPrice.toLocaleString()}</b></TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
          </div>
						</DialogContent> 
						<DialogActions> 
							
							
						</DialogActions> 
						</Dialog> 
            
					</div> 
					)} 

{this.state.loading ? <Spinner /> :""}


        
      </div>
      </Box>
    )
  }
}
export default withStyles(transactionStyles)(Transactions);
